/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Avatar from '@mui/material/Avatar';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDSelect from 'components/MDSelect';
import TableHeader from 'components/Table/TableHeader';
import TabPanel from 'components/TabPanel/TabPanel';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import nopic from 'assets/images/nopic.jpg';
import enFlag from 'assets/images/GB.png';
import thFlag from 'assets/images/TH.png';
import CustomerUpload from './CustomerUpload';
import CustomerContact from './CustomerContact';

const initAttr = {
  id: '',
  user_id: '',
  code: '',
  name: '',
  company_name: '',
  taxid: '',
  credit_day: '',
  credit_money: '',
  branch_id: '',
  tel: '',
  email: '',
  business: '',
  address: '',
  saleman_id: '',
  province_id: '',
  district_id: '',
  subdistrict_id: '',
  zipcode: '',
  country: '',
  thai_country: 'Y',
  promotion_cost: 'Y',
  image: nopic,
  line_pic: nopic,
  line_userid: '',
  line_name: '',
  line_status: '',
  line_email: '',
  catalog: '',
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  branch: 'ALL',
  page: 1,
  perPage: 10,
  pageTotal: 0,
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

function Customer() {
  const mod = 'customer';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [cboUser, setCboUser] = useState([]);
  const [cboSaleman, setCboSaleman] = useState([]);
  const [cboProvince, setCboProvince] = useState([]);
  const [cboDistrict, setCboDistrict] = useState([]);
  const [cboSubDistrict, setCboSubDistrict] = useState([]);
  const [cboBranch, setCboBranch] = useState([]);
  const [tab, setTab] = useState(0);
  const [link, setLink] = useState('');
  const [linkEn, setLinkEn] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.image_id', label: t(`attr.image`), align: 'center' },
    { id: 'tb.id', label: t(`attr.id`), align: 'center' },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.name', label: 'ชื่อลูกค้า', align: 'left' },
    { id: 'catalog', label: 'Catalog', align: 'center', sort: false },
    { id: 'tb.business', label: 'ธุรกิจ', align: 'left' },
    { id: 'tb.promotion_cost', label: 'โปรโมชั่น', align: 'left' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.province_id', label: 'ที่อยู่', align: 'left' },
    { id: 'tb.credit_day', label: 'เครดิตวัน', align: 'right' },
    { id: 'tb.credit_money', label: 'เครดิตวงเงิน', align: 'right' },
    { id: 'tb.enable', label: t(`attr.enable`), align: 'center' },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const district = (province_id) => {
    setCboDistrict([]);
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/district`, app.token, {
      lang: app.lang,
      province_id: province_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboDistrict(result.district);
      } else {
        Func.exitApp();
      }
    });
  };

  const subDistrict = (district_id) => {
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/subdistrict`, app.token, {
      lang: app.lang,
      district_id: district_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboSubDistrict(result.subdistrict);
      } else {
        Func.exitApp();
      }
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setNavigate(result.navigate);
        district(result.data.province_id, result.data.district_id);
        subDistrict(result.data.district_id);
        setAttr({
          ...result.data,
          tel: Func.maskTel(result.data.tel),
        });

        setLoading(false);
      }
    });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboProvince(result.province);
        setCboSaleman(result.saleman);
        setCboUser(result.user);
        setCboBranch(result.branch);
        setLink(result.link);
        setLinkEn(result.link_en);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return;
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      code: attr.code,
      name: attr.name,
      taxid: attr.taxid,
      saleman_id: attr.saleman_id,
      tel: Func.formatTel(attr.tel),
      business: attr.business,
      address: attr.address,
      email: attr.email,
      province_id: attr.province_id,
      subdistrict_id: attr.subdistrict_id,
      district_id: attr.district_id,
      zipcode: attr.zipcode,
      credit_day: attr.credit_day,
      credit_money: attr.credit_money,
      catalog: attr.catalog,
      thai_country: attr.thai_country,
      country: attr.country,
      branch_id: attr.branch_id,
      promotion_cost: attr.promotion_cost,
      enable: attr.enable,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.code, 3)) return false;
    if (!Func.verifyLength(attr.name, 1)) return false;

    return true;
  };

  const edit = () => {
    if (!editConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setModalEdit(false);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          code: attr.code,
          name: attr.name,
          taxid: attr.taxid,
          tel: Func.formatTel(attr.tel),
          company_name: attr.company_name,
          business: attr.business,
          address: attr.address,
          email: attr.email,
          province_id: attr.province_id,
          subdistrict_id: attr.subdistrict_id,
          district_id: attr.district_id,
          zipcode: attr.zipcode,
          credit_day: attr.credit_day,
          credit_money: attr.credit_money,
          catalog: attr.catalog,
          thai_country: attr.thai_country,
          country: attr.country,
          branch_id: attr.branch_id,
          promotion_cost: attr.promotion_cost,
          enable: attr.enable,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            init();

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setModalEdit(true);
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const del = () => {
    setLoading(true);
    setModalDel(false);
    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const upload = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/upload`, app.token, {
      id: id,
      filedata: filedata,
    }).then((result) => {
      open();
      setLoading(false);

      if (result.status === 'ok') {
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    showNotify(t(`alert.copycomplete`), 'success');
  };

  const enableDisplay = (inp) => {
    if (inp === 'Y') return <Icon color='info'>verified</Icon>;
    return <Icon color='error'>not_interested</Icon>;
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant='rounded' />;
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap', p: 0, textAlign: 'left', display: 'block' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnCatalog = (item) => {
    return (
      <MDBox className='nowrap'>
        <MDButton variant='text' color='primary' iconOnly onClick={() => copyLink(item.link)}>
          TH
        </MDButton>
        <MDButton variant='text' color='success' iconOnly onClick={() => copyLink(item.link_en)}>
          EN
        </MDButton>
      </MDBox>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    const promotionDisplay = (row) => {
      if (row.promotion_cost === 'Y') return <code>ราคาทุน</code>;

      return <code className='color-success'>ราคาขาย</code>;
    };

    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setCboDistrict([]);
                setCboSubDistrict([]);
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{imageDisplay(row)}</TableCell>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.name}
                      </TableCell>
                      <TableCell align='center'>{btnCatalog(row)}</TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.business}
                      </TableCell>
                      <TableCell align='left'>{promotionDisplay(row)}</TableCell>
                      <TableCell align='left'>{row.branch_name}</TableCell>
                      <TableCell align='left'>{row.province_name}</TableCell>
                      <TableCell align='right'>{row.credit_day}</TableCell>
                      <TableCell align='right'>{Func.numberDisplay(row.credit_money)}</TableCell>
                      <TableCell align='center'>{enableDisplay(row.enable)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderImage = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          Avatar
        </MDTypography>
        <MDBox>
          {attr.image === '' ? null : (
            <MDBox component='img' src={attr.image} alt={attr.name} width='100%' height='100%' />
          )}
          <MDBox mr={1} display='flex' justifyContent='center'>
            <CustomerUpload uploadData={upload} />
          </MDBox>
        </MDBox>
      </Paper>
    );
  };

  const renderFormInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          Information
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.image === '' ? null : (
              <MDBox component='img' src={attr.image} alt={attr.image} width='100%' height='100%' />
            )}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสลูกค้า *'
              onChange={(e) => setAttr({ ...attr, code: e.target.value })}
              value={attr.code}
              variant='standard'
              inputProps={{
                maxLength: 10,
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัส Catalog'
              onChange={(e) => setAttr({ ...attr, catalog: e.target.value })}
              value={attr.catalog}
              variant='standard'
              InputProps={{
                inputProps: {
                  maxLength: 100,
                  autoComplete: 'off',
                },
                endAdornment: (
                  <IconButton color='dark' href={`${link}${attr.catalog}`} target='_blank'>
                    <Icon>open_in_new</Icon>
                  </IconButton>
                ),
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label={`${t(`attr.enable`)}`}
              checked={attr.enable === 'Y'}
              onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormCompany = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ข้อมูลบริษัท
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อลูกค้า *'
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อนิติบุคคล'
              onChange={(e) => setAttr({ ...attr, company_name: e.target.value })}
              value={attr.company_name}
              variant='standard'
              multiline
              inputProps={{
                maxLength: 200,
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ประเภทธุรกิจ'
              onChange={(e) => setAttr({ ...attr, business: e.target.value })}
              value={attr.business}
              variant='standard'
              multiline
              inputProps={{
                maxLength: 255,
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.taxid`)}`}
              onChange={(e) => setAttr({ ...attr, taxid: Func.maskIdCard(e.target.value) })}
              value={attr.taxid}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 17,
                placeholder: '0-0000-00000-00-0',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.tel`)}`}
              onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
              value={attr.tel}
              variant='standard'
              fullWidth
              type='search'
              inputProps={{
                autoComplete: 'off',
                maxLength: 14,
                placeholder: '(000) 000 0000',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.email`)}`}
              onChange={(e) => setAttr({ ...attr, email: e.target.value })}
              value={attr.email}
              type='email'
              variant='standard'
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormAddress = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          ที่อยู่
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='ประเทศ'
              labelDefault={t(`element.please_select`)}
              data={[
                { id: 'Y', name: 'ไทย' },
                { id: 'N', name: 'ต่างประเทศ' },
              ]}
              value={attr.thai_country}
              onChange={(e) => setAttr({ ...attr, thai_country: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          {attr.thai_country === 'Y' ? null : (
            <Grid item xs={12}>
              <MDInput
                label='ระบุประเทศ'
                onChange={(e) => setAttr({ ...attr, country: e.target.value })}
                value={attr.country}
                variant='standard'
                multiline
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <MDInput
              label={`${t(`attr.address`)}`}
              onChange={(e) => setAttr({ ...attr, address: e.target.value })}
              value={attr.address}
              variant='standard'
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              fullWidth
            />
          </Grid>
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='จังหวัด'
                labelDefault={t(`element.please_select`)}
                data={cboProvince}
                value={attr.province_id}
                onChange={(e) => {
                  setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
                  district(e.target.value);
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='อำเภอ / เขต'
                labelDefault={t(`element.please_select`)}
                data={cboDistrict}
                value={attr.district_id}
                onChange={(e) => {
                  setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
                  subDistrict(e.target.value);
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDSelect
                label='ตำบล / แขวง'
                labelDefault={t(`element.please_select`)}
                data={cboSubDistrict}
                value={attr.subdistrict_id}
                onChange={(e) => {
                  cboSubDistrict.forEach((item, i) => {
                    if (item.id === e.target.value) {
                      setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                    }
                  });
                }}
                inputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
          )}
          {attr.thai_country === 'N' ? null : (
            <Grid item xs={12}>
              <MDInput
                label='รหัสไปรษณีย์'
                onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
                value={attr.zipcode}
                type='number'
                variant='standard'
                inputProps={{
                  autoComplete: 'off',
                }}
                fullWidth
              />
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  const renderEmployee = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h5' mb={3}>
          พนักงาน : {attr.user_name}
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='เปลี่ยนพนักงาน'
              labelDefault={t(`element.please_select`)}
              data={cboUser}
              value={attr.user_id}
              onChange={(e) => setAttr({ ...attr, user_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormLine = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          Line OA
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.line_pic === '' ? null : (
              <MDBox
                component='img'
                src={attr.line_pic}
                alt={attr.line_pic}
                width='100%'
                height='100%'
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อ LINE'
              value={attr.line_name}
              variant='standard'
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='สถานะ LINE'
              value={attr.line_status}
              variant='standard'
              multiline
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='E-mail LINE'
              value={attr.line_email}
              variant='standard'
              fullWidth
              disabled
              sx={{ backgroundColor: '#ffffff !important' }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormCredit = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          Credit
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='เครดิตวัน'
              onChange={(e) => setAttr({ ...attr, credit_day: e.target.value })}
              value={attr.credit_day}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>วัน</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='เครดิตวงเงิน'
              onChange={(e) => setAttr({ ...attr, credit_money: e.target.value })}
              value={attr.credit_money}
              type='number'
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position='end'>บาท</InputAdornment>,
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderFormPrice = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h5' mb={3}>
          ราคาโปรโมชั่น
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDSelect
              label='สาขา'
              labelDefault={t(`element.please_select`)}
              data={cboBranch}
              value={attr.branch_id}
              onChange={(e) => setAttr({ ...attr, branch_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ราคา'
              labelDefault={t(`element.please_select`)}
              data={[
                { id: 'Y', name: 'ราคาทุน' },
                { id: 'N', name: 'ราคาขาย' },
              ]}
              value={attr.promotion_cost}
              onChange={(e) => setAttr({ ...attr, promotion_cost: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='ชื่อลูกค้า *'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='ธุรกิจ'
            onChange={(e) => setAttr({ ...attr, business: e.target.value })}
            value={attr.business}
            variant='standard'
            multiline
            inputProps={{
              maxLength: 255,
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='นักขาย'
            labelDefault={t(`element.please_select`)}
            data={cboSaleman}
            value={attr.saleman_id}
            onChange={(e) => setAttr({ ...attr, saleman_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัสลูกค้า *'
            onChange={(e) => setAttr({ ...attr, code: e.target.value })}
            value={attr.code}
            variant='standard'
            inputProps={{
              maxLength: 10,
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.taxid`)}`}
            onChange={(e) => setAttr({ ...attr, taxid: Func.maskIdCard(e.target.value) })}
            value={attr.taxid}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 17,
              placeholder: '0-0000-00000-00-0',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.tel`)}`}
            onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
            value={attr.tel}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 14,
              placeholder: '(000) 000 0000',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.email`)}`}
            onChange={(e) => setAttr({ ...attr, email: e.target.value })}
            value={attr.email}
            type='email'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.address`)}`}
            onChange={(e) => setAttr({ ...attr, address: e.target.value })}
            value={attr.address}
            variant='standard'
            multiline
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='จังหวัด'
            labelDefault={t(`element.please_select`)}
            data={cboProvince}
            value={attr.province_id}
            onChange={(e) => {
              setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
              district(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='อำเภอ / เขต'
            labelDefault={t(`element.please_select`)}
            data={cboDistrict}
            value={attr.district_id}
            onChange={(e) => {
              setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
              subDistrict(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='ตำบล / แขวง'
            labelDefault={t(`element.please_select`)}
            data={cboSubDistrict}
            value={attr.subdistrict_id}
            onChange={(e) => {
              cboSubDistrict.forEach((item, i) => {
                if (item.id === e.target.value) {
                  setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                }
              });
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัสไปรษณีย์'
            onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
            value={attr.zipcode}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='เครดิตวัน'
            onChange={(e) => setAttr({ ...attr, credit_day: e.target.value })}
            value={attr.credit_day}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='เครดิตวงเงิน'
            onChange={(e) => setAttr({ ...attr, credit_money: e.target.value })}
            value={attr.credit_money}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDCheckbox
            label={`${t(`attr.enable`)}`}
            checked={attr.enable === 'Y'}
            onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='xs' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          {attr.name}
        </MDTypography>
        {renderInfo()}

        <Grid container mt={3}>
          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
            <MDButton variant='outlined' color='success' onClick={edit}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1}>
        <MDButton variant='gradient' color='success' onClick={edit}>
          <Icon>save</Icon>&nbsp;{t(`element.save`)}
        </MDButton>
      </MDBox>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label={t(`element.info`)} />
                    <Tab label='ผู้ติดต่อ' />
                    <Tab label={t(`attr.image`)} />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} xl={6}>
                      <MDTypography variant='h3'>{attr.code}</MDTypography>
                    </Grid>
                    <Grid item xs={12} xl={6}>
                      {renderButton()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormInfo()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormCompany()}
                      {renderFormCredit()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormAddress()}
                      {renderFormPrice()}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={3}>
                      {renderFormLine()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <MDBox className='card'>
                    <CustomerContact />
                  </MDBox>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={5} lg={3}>
                      {renderImage()}
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.customer`), t(`menu.customer`)]} />
      {renderPage()}
      {renderAdd()}
      {renderEdit()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default Customer;
